"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import Header from "@/components/Header";
import { homeFaqItems } from "@/data/homeFaq";
import { trackEvent } from "@/components/GoogleAnalytics";

const IMAGE_CDN =
  process.env.NEXT_PUBLIC_IMAGE_CDN_URL || "";

const VideoSection = dynamic(
  () => import("./HomeClient/VideoSection"),
  { ssr: true, loading: () => (
    <section className="bg-white py-20">
      <div className="container mx-auto px-4">
        <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
          Ready to get Married?
        </h2>
        <div className="mx-auto max-w-5xl">
          <div className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-xl bg-gray-900 shadow-2xl">
            <div className="relative h-0 w-full pb-[56.25%] bg-black">
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <p className="text-sm text-white">Loading...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  ) }
);

export default function HomeClient() {
  return (
    <div className="min-h-screen bg-white">
      <Header variant="transparent" />

      <main className="pt-0">
        {/* Hero section with full-width background image, 100vh height */}
        <section className="relative flex h-screen min-h-[650px] items-end overflow-hidden">
          {/* Background image */}
          <div className="absolute inset-0">
            <Image
              src={`${IMAGE_CDN}/assets/img/marathi-vadhu-var-suchak-banner.jpg`}
              alt="Nimbalkar Vadhu Var Suchak Banner"
              fill
              priority
              sizes="100vw"
              className="object-cover"
            />
          </div>

          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/35" />

          {/* Hero content */}
          <div className="container relative z-10 mb-28 mx-auto px-4">
            <div className="max-w-3xl">
              <h1
                className="hero-heading mb-8 text-[3.2rem] font-bold text-white"
                style={{ textShadow: "1px 1px 3px rgba(0, 0, 0, 0.2)" }}
              >
                निंबाळकर मराठा
                <br />
                महाराष्ट्रातील अग्रणी विवाहसंस्था
              </h1>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  href="https://marathivadhuvarsuchak.com/register"
                  className="inline-block rounded-full border border-white bg-primary-600 px-8 py-4 text-lg font-medium tracking-wide text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-primary-700"
                  onClick={() =>
                    trackEvent("register_click", {
                      location: "home_hero",
                    })
                  }
                >
                  Register Free
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Search redirect: Brides | Grooms */}
        <section className="bg-white py-16">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl text-center">
              <h2 className="mb-4 text-3xl font-bold text-gray-900">
              Educated Nimbalkar Vadhu Var Free Profile Search
              </h2>
              <p className="mx-auto mb-8 max-w-3xl text-lg text-gray-600">
              For Engineers, CA, Doctors, MBA, Graduates, Post Graduates, Government Servants, Startup Founders or Businessman Maratha Vadhu Var.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  href="/brides"
                  className="rounded-full border-2 border-primary-600 px-5 py-2 text-base font-normal text-primary-600 transition-colors hover:border-primary-700 hover:text-primary-700"
                >
                  Brides ( वधू )
                </Link>
                <Link
                  href="/grooms"
                  className="rounded-full border-2 border-primary-600 px-5 py-2 text-base font-normal text-primary-600 transition-colors hover:border-primary-700 hover:text-primary-700"
                >
                  Grooms ( वर )
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Download Our Mobile App */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto flex max-w-6xl items-center max-[599px]:flex-col max-[599px]:gap-[50px]">
              <div className="w-1/2 pr-8 max-[599px]:w-full max-[599px]:pr-0 max-[599px]:text-center">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                Download Our Marathi Vadhu Var Suchak App
                </h2>
                <p className="mb-8 text-lg text-gray-600">
                Find your perfect Nimbalkar Maratha match on the go with our mobile app. Available on both iOS and Android platforms.
                </p>
                <div className="mb-8 flex flex-col gap-4 max-[599px]:flex-row max-[599px]:justify-center sm:flex-row">
                  <Link
                    href="https://apps.apple.com/us/app/marathi-vadhu-var-suchak/id6755544958"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                    onClick={() =>
                      trackEvent("app_download_click", {
                        location: "home_app_section",
                        platform: "ios",
                      })
                    }
                  >
                    <Image
                      src={`${IMAGE_CDN}/assets/img/mvvs-app-store-button.png`}
                      alt="Download on App Store"
                      width={175}
                      height={54}
                      className="rounded-[15px] border border-[#333132] transition-opacity hover:opacity-80"
                    />
                  </Link>
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.mvvs.matrimony.mvvs_app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                    onClick={() =>
                      trackEvent("app_download_click", {
                        location: "home_app_section",
                        platform: "android",
                      })
                    }
                  >
                    <Image
                      src={`${IMAGE_CDN}/assets/img/mvvs-google-play-icon.png`}
                      alt="Get it on Google Play"
                      width={175}
                      height={54}
                      className="rounded-[15px] border border-[#333132] transition-opacity hover:opacity-80"
                    />
                  </Link>
                </div>
              </div>
              <div className="flex w-1/2 justify-center max-[599px]:w-full">
                <Image
                  src={`${IMAGE_CDN}/assets/img/mvvs-mobile-app-screen-1x.png`}
                  alt="Marathi Vadhu Var Suchak Mobile App"
                  width={244}
                  height={500}
                  className="object-contain"
                />
              </div>
            </div>
          </div>
        </section>

        {/* About Section with USPs */}
        <section className="bg-white py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl text-center">
              <h2 className="mb-6 text-3xl font-bold text-gray-900">
              About NimbalkarVadhuVar.com Search
              </h2>
              <p className="mx-auto mb-16 max-w-3xl text-lg text-gray-600">
              NimbalkarVadhuVar.com search is the online platform of 'Marathi Vadhu Var Suchak', which display educated Nimbalkar Maratha Vadhu Var list on single click. This is free online classified of 'Marathi Vadhu Var Suchak'. A most trusted and safe matrimony platform for well-educated Nimbalkar Maratha Vadhu Var.
                <br />
                <span className="font-bold">निंबाळकर वधू वर डॉट कॉम</span>
              </p>

              <div className="grid gap-8 max-[599px]:gap-12 md:grid-cols-3">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-6 flex h-24 w-24 items-center justify-center mx-auto">
                    <svg
                      className="h-[100px] w-[100px] text-primary-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V4C19 2.9 18.1 2 17 2ZM17 20H7V4H17V20Z" />
                      <path d="M12 18C13.1 18 14 17.1 14 16C14 14.9 13.1 14 12 14C10.9 14 10 14.9 10 16C10 17.1 10.9 18 12 18Z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <div className="mb-2 text-[4rem] font-bold text-gray-900 max-[599px]:text-[3rem]">
                      100%
                    </div>
                    <div className="text-[1.3rem] text-gray-900">
                      Mobile-verified profiles
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center text-center">
                  <div className="mb-6 flex h-24 w-24 items-center justify-center mx-auto">
                    <svg
                      className="h-[100px] w-[100px] text-primary-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.59 20 12 20Z" />
                      <path d="M12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <div className="mb-2 text-[4rem] font-bold text-gray-900 max-[599px]:text-[3rem]">
                      8 Years
                    </div>
                    <div className="text-[1.3rem] text-gray-900">
                      of successful matchmaking
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center text-center">
                  <div className="mb-6 flex h-24 w-24 items-center justify-center mx-auto">
                    <svg
                      className="h-[100px] w-[100px] text-primary-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <div className="mb-2 text-[4rem] font-bold text-gray-900 max-[599px]:text-[3rem]">
                      100%
                    </div>
                    <div className="text-[1.3rem] text-gray-900">
                      Security &amp; Privacy
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Female Members Promotional Offer Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <div className="flex items-center max-[599px]:flex-col max-[599px]:gap-[50px]">
                <div className="flex w-1/2 items-center justify-center max-[599px]:w-full">
                  {process.env.NEXT_PUBLIC_IMAGE_CDN_URL && (
                    <Link href="https://marathivadhuvarsuchak.com/register" className="block">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <Image
                        src={`${process.env.NEXT_PUBLIC_IMAGE_CDN_URL}/assets/img/mvvs-female-free-premium-creative-1.jpg`}
                        alt="Female Members Free Registration Offer"
                        className="h-auto w-[420px] max-w-full cursor-pointer rounded-lg object-contain shadow-lg transition-opacity hover:opacity-90"
                        width={420}
                        height={525}
                      />
                    </Link>
                  )}
                </div>
                <div className="w-1/2 pl-8 max-[599px]:w-full max-[599px]:pl-0 max-[599px]:text-center">
                  <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  Benefits to Educated Nimbalkar Martha Brides with Free Registration: 
                  </h2>
                  <ul className="space-y-4 text-lg text-gray-700">
                    <li className="flex items-start max-[599px]:justify-center">
                      <svg
                        className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Free Online Chatting</span>
                    </li>
                    <li className="flex items-start max-[599px]:justify-center">
                      <svg
                        className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Get free contact details on 1 click</span>
                    </li>
                    <li className="flex items-start max-[599px]:justify-center">
                      <svg
                        className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Send free SMS to liked profiles</span>
                    </li>
                    <li className="flex items-start max-[599px]:justify-center">
                      <svg
                        className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Free Kundli Matching</span>
                    </li>
                    <li className="flex items-start max-[599px]:justify-center">
                      <svg
                        className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>
                      Free Profiles Shortlisting
                      </span>
                    </li>
                  </ul>
                  <div className="mt-8">
                    <Link
                      href="https://marathivadhuvarsuchak.com/register"
                      className="inline-block rounded-full bg-primary-600 px-8 py-4 text-lg font-medium tracking-wide text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-primary-700"
                    >
                      Register Free
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="bg-gray-50 py-20">
          <div className="container mx-auto px-4">
            <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
              Success Stories
            </h2>
            <div className="mx-auto max-w-6xl">
              <div className="grid gap-8 md:grid-cols-3">
                <div className="rounded-2xl bg-white p-6 shadow-lg">
                  <div className="mb-4 flex items-center">
                    <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                      <span className="font-semibold text-primary-600">P</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                      Purva &amp; Ritesh Nimbalkar
                      </h3>
                      <p className="text-sm text-gray-600">Married in 2024</p>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    &ldquo;We first connected through Marathi Vadhu Var Suchak and started with a simple conversation to know each other better before marriage. Thank You!&rdquo;
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-lg">
                  <div className="mb-4 flex items-center">
                    <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100">
                      <span className="font-semibold text-secondary-600">
                        S
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                      Siddhi & Ajay Patil
                      </h3>
                      <p className="text-sm text-gray-600">Married in 2025</p>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    &ldquo;We met on the Marathi Vadhu Var Suchak app. We just talked a little here, but we connected for the whole life now after marriage.&rdquo;
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-lg">
                  <div className="mb-4 flex items-center">
                    <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                      <span className="font-semibold text-primary-600">K</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                      Komal & Kunal Pawar
                      </h3>
                      <p className="text-sm text-gray-600">Married in 2025</p>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    &ldquo;It was a great experience. We connected, and we aligned. Thanks to Marathi Vadhu Var Suchak.&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Video section – lazy-loaded to reduce main-thread blocking */}
        <VideoSection />

        {/* FAQ section – native details/summary (no JS) for lower TBT */}
        <section id="faq" className="bg-white py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
            <div className="mx-auto max-w-4xl space-y-4">
              {homeFaqItems.map((faq, index) => (
                <details
                  key={index}
                  className="group faq-details overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-gray-50 [&::-webkit-details-marker]:hidden">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <svg
                      className="h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 group-open:rotate-180"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>
                  <div className="border-t border-gray-200 px-6 py-3 pb-4">
                    <p className="leading-relaxed text-gray-600">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Journey CTA Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16">
          <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="mb-6 text-3xl font-bold text-gray-900">
              Ready to Start Your Journey?
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-600">
            Discover meaningful match through our trusted NimbalkarVadhuVar.com quick search platform built with the Maratha community's values at heart.
            </p>
            <div className="flex justify-center">
              <Link
                href="https://marathivadhuvarsuchak.com/register"
                className="rounded-full bg-primary-600 px-8 py-4 text-lg font-medium tracking-wide text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-primary-700"
              >
                Register Free
              </Link>
            </div>
          </div>
        </section>

        {/* Community Marathi Matrimony Services Section */}
        <section className="bg-white py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl text-center">
              <h2 className="mb-8 text-3xl font-bold text-gray-900">
              Nimbalkar Maratha Vadhu Var Suchak Community
              </h2>
              <p
                className="leading-relaxed text-gray-700"
                style={{ fontSize: "1.11rem" }}
              >
                Nimbalkar Vadhu Var │ Nimbalkar Vadhu Var Suchak │ Nimbalkar Vadhu Var Suchak Phaltan │ Nimbalkar Vadhu Var Suchak Photos │ Nimbalkar Vadhu Var Suchak reviews │ Nimbalkar Vadhu Var Suchak ( Maratha matrimony ) │ Nimbalkar Vadhu Var Suchak Maratha Matrimony │ Nimbalkar Vadhu Var Suchak Kendra │ Nimbalkar Vadhu Var Suchak Mandal │ Nimbalkar Vadhu Var Suchak brides │ Nimbalkar Vadhu Var Mandal │ Nimbalkar Vadhu Var Phaltan │Nimbalkar Vadhu Var Suchak Kendra Pune │ Nimbalkar Vadhu Var Pune │ Nimbalkar Vadhu Var Suchak Pune Maharashtra │ Nimbalkar Vadhu Var Suchak Pune
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
